import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { LoginResponse } from '@pgway/shared';
import { apiPost } from '../lib/api-client';
import {
  getAccessToken,
  setAccessToken,
  getCurrentUser,
  setCurrentUser,
  clearAuth,
  isAuthenticated as checkAuth,
} from '../lib/auth';

interface AuthContextType {
  user: LoginResponse['user'] | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<LoginResponse['user']>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<LoginResponse['user'] | null>(getCurrentUser());
  const [isLoading, setIsLoading] = useState(true);

  const login = useCallback(async (username: string, password: string) => {
    const res = await apiPost<LoginResponse>('/auth/login', { username, password });

    if (!res.ok) {
      throw new Error(res.error.message);
    }

    setAccessToken(res.data.accessToken);
    setCurrentUser(res.data.user);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiPost('/auth/logout');
    } catch {
      // Ignore errors on logout
    }
    clearAuth();
    setUser(null);
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      const res = await apiPost<{ accessToken: string; user?: LoginResponse['user'] }>(
        '/auth/refresh-token',
      );

      if (res.ok && res.data.accessToken) {
        setAccessToken(res.data.accessToken);
        if (res.data.user) {
          setCurrentUser(res.data.user);
          setUser(res.data.user);
        } else {
          const restoredUser = getCurrentUser();
          if (restoredUser) {
            setUser(restoredUser);
          }
        }
        return true;
      }

      clearAuth();
      setUser(null);
      return false;
    } catch {
      clearAuth();
      setUser(null);
      return false;
    }
  }, []);

  // On mount: trust sessionStorage if it has a token + user — the api-client
  // will silently refresh via httpOnly cookie on the first 401, so we don't
  // need to pre-validate. Pre-validating + clearing on failure was logging
  // users out on every refresh whenever the cookie was momentarily unavailable.
  // If there is no stored token, attempt cookie-only restore (e.g. another
  // tab still has a live session).
  useEffect(() => {
    const storedToken = getAccessToken();
    const storedUser = getCurrentUser();

    if (storedToken && storedUser) {
      setIsLoading(false);
      // Background refresh to rotate tokens. Do NOT clear on failure —
      // a real 401 from any subsequent API call will surface naturally.
      void (async () => {
        try {
          const res = await apiPost<{ accessToken: string; user?: LoginResponse['user'] }>(
            '/auth/refresh-token',
          );
          if (res.ok && res.data.accessToken) {
            setAccessToken(res.data.accessToken);
            if (res.data.user) {
              setCurrentUser(res.data.user);
              setUser(res.data.user);
            }
          }
        } catch {
          // ignore — keep existing session; api-client handles real 401s
        }
      })();
    } else {
      // No stored session — try cookie-only restore once.
      refreshSession().finally(() => setIsLoading(false));
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: checkAuth() && user !== null,
        isLoading,
        login,
        logout,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
